import React from 'react';
import { ArrowUpRight, ArrowDownRight, ArrowUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTranslationDir } from '../../hooks/useTranslationDir';

export const TransactionTable = ({
  transactions,
  sortBy,
  sortOrder,
  onSort
}) => {
  const { t, i18n } = useTranslation();
  const { isRTL } = useTranslationDir();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(i18n.language === 'ar' ? 'ar' : 'en-US', {
      style: 'currency',
      currency: 'EGP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(i18n.language === 'ar' ? 'ar' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown size={16} className="text-slate-400" />;
    return sortOrder === 'asc' ?
      <ArrowUpRight size={16} className="text-blue-600" /> :
      <ArrowDownRight size={16} className="text-blue-600" />;
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th
                className="px-6 py-3 rtl:text-start text-xs font-medium text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                onClick={() => onSort('createdAt')}
              >
                <div className="flex items-center space-x-1">
                  <span>{t('transactions.date')}</span>
                  <SortIcon field="createdAt" />
                </div>
              </th>
              <th
                className="px-6 py-3 rtl:text-start text-xs font-medium text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                onClick={() => onSort('type')}
              >
                <div className="flex items-center space-x-1">
                  <span>{t('transactions.type')}</span>
                  <SortIcon field="type" />
                </div>
              </th>
              <th className="px-6 py-3 rtl:text-start text-xs font-medium text-slate-700 uppercase tracking-wider">
                {t('Description')}
              </th>
              <th className="px-6 py-3 rtl:text-start text-xs font-medium text-slate-700 uppercase tracking-wider">
                {t('transactions.category')}
              </th>
              <th
                className="px-6 py-3 rtl:text-start text-xs font-medium text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100"
                onClick={() => onSort('amount')}
              >
                <div className="flex items-center space-x-1">
                  <span>{t('transactions.amount')}</span>
                  <SortIcon field="amount" />
                </div>
              </th>
              <th className="px-6 py-3 rtl:text-start text-xs font-medium text-slate-700 uppercase tracking-wider">
                {t('transactions.paymentMethod')}
              </th>
              <th className="px-6 py-3 rtl:text-start text-xs font-medium text-slate-700 uppercase tracking-wider">
                {t('transactions.user')}
              </th>
              <th className="px-6 py-3 rtl:text-start text-xs font-medium text-slate-700 uppercase tracking-wider">
                {t('transactions.reference')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {transactions.map((transaction) => (
              <tr key={transaction.id} className="hover:bg-slate-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                  {formatDate(transaction.createdAt)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${transaction.type === 'income'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                      }`}
                  >
                    {transaction.type === 'income' ? (
                      <ArrowUpRight size={14} className="mr-1" />
                    ) : (
                      <ArrowDownRight size={14} className="mr-1" />
                    )}
                    {t(`transactions.${transaction.type}`)}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-slate-900">
                  <div className="max-w-xs truncate">{`${t(`transactions.${transaction.source}`)} ${t(transaction.transactionCode)}`}</div>
                  {(transaction.guestName || transaction.vendorName) && (
                    <div className="text-xs text-slate-500 mt-1">
                      {transaction.guestName || transaction.vendorName}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                  {t(transaction.category === 'deposit' ? 'transactions.deposit' : transaction.category)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`text-sm font-semibold ${transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
                      }`}
                  >
                    {transaction.type === 'income' ? '+' : '-'}
                    {transaction.amount < 1 ? formatCurrency(transaction.amount) : formatCurrency(transaction.amount)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                  {t(transaction.paymentMethod)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                  {transaction.userName}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 font-mono">
                  {transaction.reference}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
